"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default font-medium text-sm uppercase tracking-widest text-foreground/80 hover:text-primary transition-all duration-300">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="font-default font-semibold text-lg uppercase tracking-wider text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="226648" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-foreground/10" : "border-b border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661389/img/benchmark_geometric_logo.png" className="h-8 w-auto object-contain" alt="Benchmark Media Consulting" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="50">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-6 flex-shrink-0">
          <Button className="animate-pulse shadow-lg shadow-primary/20 font-default font-bold uppercase tracking-widest rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
            Get Started
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-foreground/10 md:hidden shadow-2xl">
          <MenuProvider menu_id="50">
            <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 pt-2 flex flex-col gap-4 border-t border-border/50">
            <Button className="w-full animate-pulse shadow-md shadow-primary/20 font-default font-bold uppercase tracking-widest rounded-sm bg-primary text-primary-foreground" onClick={() => setIsMobileMenuOpen(false)}>
              Get Started
            </Button>
          </div>
        </div>}
    </header>;
}